import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { User, DollarSign, Building2 } from 'lucide-react'
import { searchContactsQuick, searchDealsQuick, searchCompaniesQuick } from '@/features/hubspot/services/hubspotApi'
import { hubspotRecordUrl } from '@/lib/hubspotUrl'
import { STATIC_SEARCH_ITEMS } from '../data/staticIndex'
import type { SearchResult, SearchResultGroup, StaticSearchItem } from '../types'

type HubSpotConn = { connected: boolean; portalId: number | null; uiDomain: string | null } | null | undefined

const CATEGORY_ORDER: SearchResult['category'][] = ['Pages', 'Settings', 'Contacts', 'Deals', 'Leads']

function matchesStatic(item: StaticSearchItem, q: string): boolean {
  if (item.title.toLowerCase().includes(q)) return true
  if (item.subtitle?.toLowerCase().includes(q)) return true
  return item.keywords?.some((k) => k.toLowerCase().includes(q)) ?? false
}

function openInHubSpot(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer')
}

// Live search across the workspace's connected CRM (contacts/deals/leads via
// HubSpot) plus a static index of in-app pages and settings sections. Results
// are grouped by category; CRM results open the record in HubSpot (there's no
// in-app contacts/deals page to route into today), static results navigate
// in-app.
export function useGlobalSearch(query: string, workspaceId: string | null, hubspot: HubSpotConn) {
  const navigate = useNavigate()
  const trimmed = query.trim()
  const q = trimmed.toLowerCase()
  const [crmResults, setCrmResults] = useState<SearchResult[]>([])
  const [crmLoading, setCrmLoading] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!trimmed || !workspaceId || !hubspot?.connected) {
      setCrmResults([])
      setCrmLoading(false)
      return
    }

    if (timerRef.current) clearTimeout(timerRef.current)
    setCrmLoading(true)

    timerRef.current = setTimeout(async () => {
      try {
        const [contacts, deals, companies] = await Promise.all([
          searchContactsQuick(workspaceId, trimmed).catch(() => []),
          searchDealsQuick(workspaceId, trimmed).catch(() => []),
          searchCompaniesQuick(workspaceId, trimmed).catch(() => []),
        ])

        const results: SearchResult[] = []

        for (const c of contacts) {
          const url = hubspotRecordUrl(hubspot.portalId, 'contact', c.id, hubspot.uiDomain)
          if (!url) continue
          results.push({
            id: `contact-${c.id}`,
            category: 'Contacts',
            title: c.name || 'Unnamed contact',
            subtitle: [c.company, c.email].filter(Boolean).join(' · '),
            icon: User,
            onSelect: () => openInHubSpot(url),
          })
        }

        for (const d of deals) {
          const url = hubspotRecordUrl(hubspot.portalId, 'deal', d.id, hubspot.uiDomain)
          if (!url) continue
          results.push({
            id: `deal-${d.id}`,
            category: 'Deals',
            title: d.name || 'Unnamed deal',
            subtitle: [d.stage, d.amount].filter(Boolean).join(' · '),
            icon: DollarSign,
            onSelect: () => openInHubSpot(url),
          })
        }

        for (const co of companies) {
          const url = hubspotRecordUrl(hubspot.portalId, 'company', co.id, hubspot.uiDomain)
          if (!url) continue
          results.push({
            id: `lead-${co.id}`,
            category: 'Leads',
            title: co.name || 'Unnamed account',
            subtitle: [co.industry, co.domain].filter(Boolean).join(' · '),
            icon: Building2,
            onSelect: () => openInHubSpot(url),
          })
        }

        setCrmResults(results)
      } catch {
        setCrmResults([])
      } finally {
        setCrmLoading(false)
      }
      // Quicker turnaround than the 250ms chat @mention search uses — this bar has
      // no separate "typing indicator", so a snappier debounce reads as more responsive.
    }, 200)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [trimmed, workspaceId, hubspot?.connected, hubspot?.portalId, hubspot?.uiDomain])

  const groups = useMemo<SearchResultGroup[]>(() => {
    if (!trimmed) return []

    const staticResults: SearchResult[] = STATIC_SEARCH_ITEMS.filter((item) => matchesStatic(item, q)).map((item) => ({
      id: item.id,
      category: item.category,
      title: item.title,
      subtitle: item.subtitle,
      icon: item.icon,
      onSelect: () => navigate(item.href),
    }))

    const byCategory = new Map<SearchResult['category'], SearchResult[]>()
    for (const r of [...staticResults, ...crmResults]) {
      const list = byCategory.get(r.category) ?? []
      list.push(r)
      byCategory.set(r.category, list)
    }

    return CATEGORY_ORDER.filter((c) => byCategory.has(c)).map((category) => ({
      category,
      items: byCategory.get(category)!,
    }))
  }, [trimmed, q, crmResults, navigate])

  return { groups, loading: crmLoading, hasQuery: Boolean(trimmed) }
}
